import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight } from "@phosphor-icons/react";
import { APPLICATION_URL, footerCopy, gameCopy } from "../../content";
import { useGame } from "../../game/GameContext";
import { AutterWordmarkOnDark, CaptainPatchImage } from "../brand/AutterBrand";

const easeOut = [0.16, 1, 0.3, 1] as const;

export function Footer() {
  const reduce = useReducedMotion();
  const { started, reset } = useGame();
  const [sosOpen, setSosOpen] = useState(false);
  const sosButtonRef = useRef<HTMLButtonElement>(null);
  const external = APPLICATION_URL.startsWith("http");

  useEffect(() => {
    if (!sosOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSosOpen(false);
        sosButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sosOpen]);

  return (
    <footer className="relative overflow-hidden border-t border-foam/10 bg-[#18264b] py-16 text-foam md:py-20">
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-56 bg-[url('/autter-assets/autter-hero-poster.jpg')] bg-cover bg-[center_68%] opacity-[0.16]" />
      <div aria-hidden className="absolute inset-0 bg-[linear-gradient(180deg,#18264b_28%,rgba(24,38,75,.82)_100%)]" />
      <div className="relative mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <motion.p
          initial={reduce ? false : { opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="display-type max-w-[22ch] text-[clamp(2rem,5vw,4rem)] text-foam"
        >
          {footerCopy.statement}
        </motion.p>

        <div className="mt-12 flex flex-wrap items-end justify-between gap-8">
          <div className="flex items-end gap-5">
            <CaptainPatchImage pose="welcome" className="h-32 w-32 shrink-0" />
            <div>
              <AutterWordmarkOnDark className="h-8 w-auto" />
              <p className="mt-3 max-w-sm text-sm text-foam/70">{footerCopy.meta}</p>
              <p className="mt-1 text-sm text-foam/50">Same Boat · © {new Date().getFullYear()}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <details className="max-w-xs">
              <summary className="flex min-h-11 cursor-pointer list-none items-center font-mono text-xs text-foam/65 underline-offset-4 select-none hover:text-foam hover:underline [&::-webkit-details-marker]:hidden">
                captain's log
              </summary>
              <p className="mt-2 max-w-xs font-mono text-xs text-foam/70">{footerCopy.log}</p>
            </details>

            {started && (
              <button
                type="button"
                onClick={reset}
                className="min-h-11 cursor-pointer font-mono text-xs text-foam/65 underline-offset-4 hover:text-foam hover:underline"
              >
                {gameCopy.hud.reset}
              </button>
            )}

            <div className="relative">
              <button
                ref={sosButtonRef}
                type="button"
                aria-expanded={sosOpen}
                aria-controls="sos-popover"
                onClick={() => setSosOpen((open) => !open)}
                className="size-11 cursor-pointer rounded-full border-2 border-foam/60 font-mono text-xs font-semibold text-foam transition-colors duration-200 hover:border-[#35f1c6] hover:bg-[#35f1c6] hover:text-[#18264b]"
              >
                {footerCopy.sos.label}
              </button>

              <AnimatePresence>
                {sosOpen && (
                  <motion.div
                    id="sos-popover"
                    initial={reduce ? false : { opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.25, ease: easeOut }}
                    className="absolute right-0 bottom-full z-10 mb-3 w-64 rounded-md border-2 border-ink bg-foam p-5 text-ink shadow-card"
                  >
                    <p className="font-semibold">{footerCopy.sos.title}</p>
                    <p className="mt-2 text-sm text-ink-soft">{footerCopy.sos.body}</p>
                    <a
                      href={APPLICATION_URL}
                      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
                      className="mt-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold underline underline-offset-4"
                    >
                      {footerCopy.sos.cta}
                      <ArrowRight weight="bold" className="size-4 shrink-0" aria-hidden />
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
